/**
 * ViewContextStore
 *
 * Manages the "active view" context for users who may hold both
 * carrier and shipper profiles simultaneously.
 *
 * ─── Today ───────────────────────────────────────────────────────
 *   • User has only one profile (carrier OR shipper).
 *   • activeView initialises from PermissionService automatically.
 *   • canSwitchView = false  →  no switcher shown in the UI.
 *
 * ─── Future (dual profile) ───────────────────────────────────────
 *   • User has both profiles (ofertaLance = true AND ofertaFrete = true).
 *   • canSwitchView = true  →  header shows a view-switcher button.
 *   • setView() / toggleView() change the active context instantly.
 *   • Profile guards still verify PERMISSION (not activeView), so a
 *     shipper cannot reach carrier routes even with activeView = 'carrier'.
 *   • Sidebar menu and /app redirect are driven by activeView.
 */
import { Injectable, inject, signal, computed, effect, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PermissionService } from '../core/auth/permission.service';

export type ActiveView = 'carrier' | 'shipper';

const VIEW_CONTEXT_KEY = 'active-view';

@Injectable({ providedIn: 'root' })
export class ViewContextStore {
  private readonly permissionService = inject(PermissionService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // ─── Permission mirrors (convenience, same signals as PermissionService) ─
  readonly isCarrier = this.permissionService.isCarrier;
  readonly isShipper = this.permissionService.isShipper;

  /**
   * The currently active view.
   * Persisted in localStorage so the user resumes their last context
   * after a page reload.
   */
  readonly activeView = signal<ActiveView>(this.resolveInitialView());

  /**
   * True when the user holds BOTH carrier and shipper profiles.
   * Used by the header to decide whether to render the view switcher.
   */
  readonly canSwitchView = computed(
    () => this.permissionService.isCarrier() && this.permissionService.isShipper()
  );

  /**
   * The base route for the currently active view.
   * Used by viewRedirectGuard to redirect /app to the correct home.
   */
  readonly activeBaseRoute = computed<string>(() =>
    this.activeView() === 'carrier' ? '/app/carriers' : '/app/shipper'
  );

  constructor() {
    // Persist activeView changes to localStorage
    effect(() => {
      const view = this.activeView();
      if (this.isBrowser) {
        localStorage.setItem(VIEW_CONTEXT_KEY, view);
      }
    });

    // Correct activeView once auth state resolves with real user data.
    // Handles the case where a stale localStorage value (e.g. 'carrier')
    // was inherited by a shipper-only user, or vice-versa.
    effect(() => {
      const isCarrier = this.permissionService.isCarrier();
      const isShipper = this.permissionService.isShipper();
      const current = this.activeView();

      if (!isCarrier && isShipper && current !== 'shipper') {
        this.activeView.set('shipper');
      } else if (isCarrier && !isShipper && current !== 'carrier') {
        this.activeView.set('carrier');
      }
    });
  }

  /**
   * Switch the active view. Only meaningful when the user has both profiles.
   * Guards enforce access at the route level regardless of this value.
   */
  setView(view: ActiveView): void {
    this.activeView.set(view);
  }

  /**
   * Toggle between carrier and shipper.
   * Intended for the view-switcher button in the header.
   */
  toggleView(): void {
    this.activeView.update(v => (v === 'carrier' ? 'shipper' : 'carrier'));
  }

  // ─── Private ─────────────────────────────────────────────────────

  /**
   * Resolves the initial active view on store instantiation:
   *   1. Restores the persisted preference from localStorage.
   *   2. Falls back to the profile flags from PermissionService.
   *   3. Defaults to 'carrier' as a safe fallback while auth resolves.
   */
  private resolveInitialView(): ActiveView {
    if (this.isBrowser) {
      const saved = localStorage.getItem(VIEW_CONTEXT_KEY) as ActiveView | null;
      if (saved === 'carrier' || saved === 'shipper') {
        return saved;
      }
    }

    if (this.permissionService.isCarrier()) return 'carrier';
    if (this.permissionService.isShipper()) return 'shipper';

    return 'carrier'; // safe default while auth state resolves
  }
}
